/**
 * The shelf of books a learner is part-way through.
 *
 * The rules — what belongs on it, in what order, how far in each one is — live in
 * `#/lib/flashbookShelf`, which is pure. This is the surface: a row of covers at
 * the top of the feed, and the way back into each book at the page it was left on.
 *
 * Four decisions shape it:
 *
 *  - **It is the learner's own device that answers.** The shelf reads the place
 *    store directly rather than asking the network, so it is on screen on the first
 *    frame, it works on a plane, and it costs no request to draw. The book's *cover*
 *    is the one thing that needs a record, and it is asked for per card — through
 *    the same cached query the reader uses, so opening a book from the shelf is a
 *    cache hit rather than a second fetch.
 *
 *  - **A card is legible before the cover arrives.** Each card paints a page in the
 *    book's own theme with the title set on it, and only swaps in the artwork when
 *    it lands. That is the feed card's rule for the same reason: a shelf that is
 *    grey boxes until the network answers reads as broken, and the cover is how a
 *    reader recognises a book they have already seen.
 *
 *  - **Nothing at all when there is nothing to continue.** An empty shelf is not
 *    an empty state — the front door of a feed has no room for a box that says a
 *    learner has not read anything, and every learner who has not read a book does
 *    not have one.
 *
 *  - **It re-reads when the screen is focused.** A learner who finishes a book and
 *    comes back should not still be offered it, and one who read on should find the
 *    page they left rather than the page they were on when Home last rendered.
 *    Focus is the moment both of those are true.
 */
import {useCallback, useMemo, useState} from 'react'
import {Pressable, ScrollView, StyleSheet, View} from 'react-native'
import {Image} from 'expo-image'
import {useLingui} from '@lingui/react/macro'
import {useFocusEffect, useNavigation} from '@react-navigation/native'

import {readProgressIndex} from '#/lib/flashbookProgressStore'
import {
  buildFlashBookShelf,
  type FlashBookShelfItem,
} from '#/lib/flashbookShelf'
import {type NavigationProp} from '#/lib/routes/types'
import {useFlashbookByUriQuery} from '#/state/queries/flashbook'
import {atoms as a, useTheme} from '#/alf'
import {Text} from '#/components/Typography'
import {
  getFlashbookTheme,
  listFlashbookThemeItems,
} from '#/themes-flashbook/registry'

/**
 * How wide a card is.
 *
 * Fixed rather than a fraction of the row, because a shelf is a row of *objects*
 * and objects do not change size with the window: a phone shows three and a
 * desktop shows eight, which is what a shelf of books does on a real one.
 */
const CARD_WIDTH = 124

export function FlashBookShelf() {
  const {t: l} = useLingui()
  const t = useTheme()
  const [items, setItems] = useState<FlashBookShelfItem[]>(() =>
    buildFlashBookShelf(readProgressIndex()),
  )

  /*
   * Re-read on focus, which is the one moment the answer can have changed while
   * this screen was not on: a book finished in the reader, or read on a page.
   * Deliberately not an effect on every render — storage is synchronous but not
   * free, and the shelf's own contents only move when the learner leaves Home.
   */
  useFocusEffect(
    useCallback(() => {
      setItems(buildFlashBookShelf(readProgressIndex()))
    }, []),
  )

  if (!items.length) return null

  return (
    <View testID="flashbook-shelf" style={[a.py_md]}>
      <View style={[a.flex_row, a.align_baseline, a.justify_between, a.px_lg]}>
        <Text style={[a.text_md, a.font_bold, t.atoms.text]}>
          {l`Continue reading`}
        </Text>
        <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
          {l`${items.length} books`}
        </Text>
      </View>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={[a.gap_md, a.px_lg, a.pt_md]}>
        {items.map((item, index) => (
          <ShelfCard key={item.bookUri} item={item} index={index} />
        ))}
      </ScrollView>
    </View>
  )
}

/**
 * One book on the shelf: its cover, its title, and where the reader is in it.
 *
 * The cover is the book's *cover* when the record has one and a page of its own
 * theme when it does not, so the two cases are the same size and the row does not
 * jump as records arrive.
 */
function ShelfCard({item, index}: {item: FlashBookShelfItem; index: number}) {
  const {t: l} = useLingui()
  const t = useTheme()
  const navigation = useNavigation<NavigationProp>()
  const {data} = useFlashbookByUriQuery(item.bookUri)

  const themeId = useMemo(() => {
    const id = data?.themeId
    return id && listFlashbookThemeItems().some(entry => entry.id === id)
      ? id
      : 'storybook'
  }, [data?.themeId])
  const tokens = useMemo(() => getFlashbookTheme(themeId).tokens, [themeId])

  const coverUri = data?.cover?.imageUri ?? data?.cover?.videoThumbnailUri
  const title = item.bookTitle || l`Untitled book`
  const place =
    item.pageCount > 0
      ? l`Page ${item.page} of ${item.pageCount}`
      : l`Page ${item.page}`

  const onOpen = useCallback(() => {
    /*
     * Opened by the *record*, which is what the reader reads: the same book
     * reached from a post, a message or this shelf is the same read.
     *
     * And opened *at* the place, because that is the whole of what the card just
     * said: the learner pressed a book that says "Page 3 of 4", and landing on its
     * cover would ask them to say it again. The reader takes the page from storage
     * itself, so nothing about it travels in the route — only the fact that this way
     * in means to continue. A book with nothing to continue (a read that never left
     * its first page) opens on its cover as it always did, because there is no page
     * to promise.
     */
    navigation.navigate('FlashBookPlay', {
      flashbookid: item.bookUri,
      resume: true,
    })
  }, [item.bookUri, navigation])

  return (
    <Pressable
      testID={`flashbook-shelf-card--${index}`}
      accessibilityRole="button"
      accessibilityLabel={l`Continue ${title}`}
      accessibilityHint={place}
      onPress={onOpen}
      style={({pressed}) => [
        {width: CARD_WIDTH},
        a.gap_xs,
        pressed && {opacity: 0.85},
      ]}>
      <View
        style={[
          styles.cover,
          {
            backgroundColor: tokens.pageBg,
            borderColor: tokens.pageBorder,
          },
        ]}>
        {coverUri ? (
          <Image
            source={{uri: coverUri}}
            contentFit="cover"
            style={StyleSheet.absoluteFill}
            accessibilityIgnoresInvertColors
          />
        ) : (
          /*
            No artwork yet: a page of the book's own theme, marked with its
            accent, rather than a grey box. The title is *under* the cover in
            both cases, so the two states are the same card with a different
            face — and the row does not jump when the record lands.
          */
          <View style={[a.flex_1, a.justify_start, a.p_sm]}>
            <View
              style={[
                styles.spine,
                {backgroundColor: tokens.accent, opacity: 0.9},
              ]}
            />
          </View>
        )}
        {/*
          The rail sits on the cover rather than under it: a shelf is scanned by
          picture, and how far in is part of the picture of a book being read.
        */}
        {item.pageCount > 0 && (
          <View
            testID={`flashbook-shelf-rail--${index}`}
            style={[styles.rail, {backgroundColor: tokens.cardBg}]}>
            <View
              style={[
                styles.railFill,
                {
                  backgroundColor: tokens.progressFill,
                  width: `${Math.round(item.pct * 100)}%`,
                },
              ]}
            />
          </View>
        )}
      </View>
      <Text numberOfLines={2} style={[a.text_xs, a.font_bold, t.atoms.text]}>
        {title}
      </Text>
      <Text
        testID={`flashbook-shelf-place--${index}`}
        numberOfLines={1}
        style={[a.text_xs, {color: tokens.accent}]}>
        {place}
      </Text>
    </Pressable>
  )
}

const styles = StyleSheet.create({
  cover: {
    width: '100%',
    aspectRatio: 4 / 5,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 8,
    overflow: 'hidden',
    justifyContent: 'flex-end',
  },
  spine: {
    width: 22,
    height: 3,
    borderRadius: 2,
  },
  rail: {
    height: 5,
    // A hairline of the page's own card colour keeps the rail from dissolving
    // into a dark theme's cover, where the track is invisible against the page.
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: 'rgba(0,0,0,0.08)',
  },
  railFill: {
    height: '100%',
  },
})
